import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Sparkle, Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = {
  name: "", brand: "", tone_of_voice: "", strategy: "",
  documents: [], products: [], faqs: [], assets: [],
};

function AiField({ field, label, value, onChange }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const assist = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/knowledge/ai-assist", { field, context: value });
      onChange(data.suggestion);
      toast.success("AI suggestion applied");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button type="button" variant="outline" size="sm" className="rounded-sm h-7 text-xs" onClick={assist} disabled={loading} data-testid={`ai-assist-${field}`}>
          <Sparkle size={13} className="mr-1" /> {loading ? t("knowledge.aiAssisting") : t("knowledge.aiAssist")}
        </Button>
      </div>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} className="rounded-sm min-h-24" data-testid={`field-${field}`} />
    </div>
  );
}

export default function KnowledgeItemEditor() {
  const { t } = useTranslation();
  const { kbId, itemId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(EMPTY);
  const isNew = !itemId || itemId === "new";

  useEffect(() => {
    if (!isNew) api.get(`/knowledge/items/${itemId}`).then((r) => setItem(r.data)).catch(() => {});
  }, [itemId, isNew]);

  const set = (k, v) => setItem((p) => ({ ...p, [k]: v }));

  const addProduct = () => set("products", [...item.products, { name: "", description: "", price: "" }]);
  const addFaq = () => set("faqs", [...item.faqs, { question: "", answer: "" }]);
  const addDoc = () => set("documents", [...item.documents, { title: "", content: "" }]);

  const updateArr = (key, idx, field, val) => {
    const arr = [...item[key]];
    arr[idx] = { ...arr[idx], [field]: val };
    set(key, arr);
  };
  const removeArr = (key, idx) => set(key, item[key].filter((_, i) => i !== idx));

  const save = async () => {
    if (!item.name.trim()) { toast.error("Name is required"); return; }
    const payload = {
      name: item.name, brand: item.brand, tone_of_voice: item.tone_of_voice, strategy: item.strategy,
      documents: item.documents, products: item.products, faqs: item.faqs, assets: item.assets,
    };
    try {
      if (isNew) await api.post(`/knowledge/bases/${kbId}/items`, payload);
      else await api.put(`/knowledge/items/${itemId}`, payload);
      toast.success("Saved");
      navigate(`/knowledge/${kbId}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate(`/knowledge/${kbId}`)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="editor-back-button">
        <ArrowLeft size={16} /> {t("common.back")}
      </button>
      <PageHeader
        title={isNew ? t("knowledge.newItem") : item.name}
        action={<Button className="rounded-sm" onClick={save} data-testid="save-item-button"><FloppyDisk size={16} className="mr-2" />{t("common.save")}</Button>}
      />

      <div className="space-y-6">
        <div className="bg-card border border-border rounded-sm p-6 space-y-4">
          <div className="space-y-2">
            <Label>{t("knowledge.itemName")}</Label>
            <Input value={item.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm" data-testid="item-name-input" />
          </div>
          <AiField field="brand" label={t("knowledge.brand")} value={item.brand} onChange={(v) => set("brand", v)} />
          <AiField field="tone_of_voice" label={t("knowledge.tone")} value={item.tone_of_voice} onChange={(v) => set("tone_of_voice", v)} />
          <AiField field="strategy" label={t("knowledge.strategy")} value={item.strategy} onChange={(v) => set("strategy", v)} />
        </div>

        {/* Products */}
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold">{t("knowledge.products")}</h3>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={addProduct} data-testid="add-product-button"><Plus size={14} className="mr-1" />{t("knowledge.addProduct")}</Button>
          </div>
          <div className="space-y-3">
            {item.products.map((p, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_100px_auto] gap-2 items-start">
                <Input placeholder={t("common.name")} value={p.name} onChange={(e) => updateArr("products", i, "name", e.target.value)} className="rounded-sm" data-testid={`product-name-${i}`} />
                <Input placeholder={t("knowledge.description")} value={p.description} onChange={(e) => updateArr("products", i, "description", e.target.value)} className="rounded-sm" />
                <Input placeholder={t("knowledge.price")} value={p.price} onChange={(e) => updateArr("products", i, "price", e.target.value)} className="rounded-sm font-mono" />
                <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => removeArr("products", i)}><Trash size={16} /></Button>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold">{t("knowledge.faqs")}</h3>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={addFaq} data-testid="add-faq-button"><Plus size={14} className="mr-1" />{t("knowledge.addFaq")}</Button>
          </div>
          <div className="space-y-3">
            {item.faqs.map((f, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-start">
                <Input placeholder={t("knowledge.question")} value={f.question} onChange={(e) => updateArr("faqs", i, "question", e.target.value)} className="rounded-sm" data-testid={`faq-q-${i}`} />
                <Input placeholder={t("knowledge.answer")} value={f.answer} onChange={(e) => updateArr("faqs", i, "answer", e.target.value)} className="rounded-sm" />
                <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => removeArr("faqs", i)}><Trash size={16} /></Button>
              </div>
            ))}
          </div>
        </div>

        {/* Documents */}
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold">{t("knowledge.documents")}</h3>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={addDoc} data-testid="add-doc-button"><Plus size={14} className="mr-1" />{t("knowledge.addDocument")}</Button>
          </div>
          <div className="space-y-3">
            {item.documents.map((d, i) => (
              <div key={i} className="space-y-2 border border-border rounded-sm p-3">
                <div className="flex gap-2">
                  <Input placeholder={t("knowledge.title2")} value={d.title} onChange={(e) => updateArr("documents", i, "title", e.target.value)} className="rounded-sm" data-testid={`doc-title-${i}`} />
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive shrink-0" onClick={() => removeArr("documents", i)}><Trash size={16} /></Button>
                </div>
                <Textarea placeholder={t("knowledge.content")} value={d.content} onChange={(e) => updateArr("documents", i, "content", e.target.value)} className="rounded-sm" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
